import React, { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import withStyles from 'src/common/withStyles';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import FormError from '../../../../common/components/FormError';
import Input from '../../../../common/components/inputs/Input';
import Button from '../../../../common/components/Button';
import { decorateComponent, rffSubmitResponse, unpackApiError } from '../../../../common/helpers';
import { rffValidators, combineValidators } from '../../../../common/validation';
import { MFALoginAction, resendMFACodeAction } from '../../../../common/ducks/user';
import { ReduxState } from '../../../../types/reduxState';
import { MFAMethod } from '../../../../types/common/MFAMethods';

import s from './OtpForm.module.scss';

const codeValidate = combineValidators([
  rffValidators.required({ message: 'Please enter authentication code' }),
  rffValidators.maxLength({ message: 'Maximum length: 20 characters', length: 20 }),
  rffValidators.number({ message: 'Invalid format, try again.' }),
]);

type OtpFormProps = {
  currentMFAMethod: MFAMethod;
  onSubmitSuccess: () => void;
};

const OtpForm: FC<OtpFormProps> = ({
  currentMFAMethod,
  onSubmitSuccess,
}) => {
  const dispatch = useDispatch<ThunkDispatch<ReduxState, {}, Action>>();
  const [resendError, setResendError] = useState<string | null>(null);
  const onSubmit = useCallback(
    async (values: { otp: string }) => dispatch(MFALoginAction(
      {
        ...values,
        method: currentMFAMethod,
      },
    ))
      .then(onSubmitSuccess)
      .catch(rffSubmitResponse()),
    [
      dispatch,
      onSubmitSuccess,
      currentMFAMethod,
    ],
  );
  const onResendClick = useCallback(
    async () => dispatch(resendMFACodeAction({ method: currentMFAMethod }))
      .then(() => {
        if (!resendError) setResendError(null);
      })
      .catch(async (error) => {
        const { detail } = await unpackApiError(error);
        setResendError(detail || 'Error resending code');
      }),
    [dispatch, currentMFAMethod, resendError],
  );
  const canResendCode = currentMFAMethod === 'sms' || currentMFAMethod === 'push';
  return (
    <Form
      onSubmit={onSubmit}
    >
      {({ handleSubmit, submitError, submitting }) => (
        <form
          method="POST"
          onSubmit={handleSubmit}
          className={s.root}
        >
          <FormError error={submitError || resendError} />
          <fieldset>
            <Field
              name="otp"
              placeholder="Authentication code"
              type="text"
              component={Input}
              validate={codeValidate}
              autoFocus
            />
          </fieldset>
          <div className={s.actionGroup}>
            <Button
              type="submit"
              submitting={submitting}
              fullWidth
            >
              Verify
            </Button>
          </div>
          { canResendCode && (
            <button
              className={s.resendCodeButton}
              type="button"
              onClick={onResendClick}
            >
              Resend Code
            </button>
          ) }
        </form>
      )}
    </Form>
  );
};

const decorators = [
  withStyles(s),
];

export default decorateComponent(OtpForm, decorators);
